import React, { useContext, useEffect, useState } from 'react';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { Delete, Edit } from '@material-ui/icons';

import moment from 'moment';

import PropTypes from 'prop-types';

import CustomCheckBox from '../../fields/customCheckBox';
import DeleteToolBar from '../../fields/deleteToolBar';
import TablePaginationActions from '../../fields/tablePaginationActions';

import { defaultRowsPerPage, defaultSortOrder, rowsPerPageOptions } from '../../../utils/constants';
import { getComparator, stableSort } from '../../../utils/helpers';

import translations from '../../../languages/i18n';

import fieldStyles from '../../../styles/fieldStyles';
import { customOptionsTableStyles, tableTitle } from '../../../styles/formElementsStyles';

import { SessionContext } from '../../../utils/context';

export default function PatientEnrolmentList({ administeredProgramsList, handleEditEnrolment, handleRemove, handleSelectedEnrolment, enrolmentsData, selectedEnrolments }) {
  const sessionContext = useContext(SessionContext);

  const classesField = fieldStyles();
  const classesTable = tableTitle();
  const bgColors = customOptionsTableStyles();

  const [adaptedEnrolmentsData, setAdaptedEnrolmentsData] = useState([]);
  const [order, setOrder] = React.useState(defaultSortOrder); // Indica l'ordre per defecte
  const [orderBy, setOrderBy] = React.useState('createdAt'); // Indica la columna per la que ordenam per defecte
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  // Adapta els Enrolments al format que es necessita per ordenar-los emprant la funció gènerica de comparació (la que es crida a getComparator)
  const adaptEnrolmentsData = (enrolments) => {
    const out = [];
    enrolments.map((enrolment) => {
      out.push({
        ...enrolment,
        name: enrolment.program.name,
        sharedId: enrolment.program.sharedId,
      });
    });
    return out;
  };

  useEffect(() => {
    const adaptedEnrolments = adaptEnrolmentsData(enrolmentsData);
    setAdaptedEnrolmentsData(adaptedEnrolments);
    // Ordenam de nou el llistat per la columna i sentit per defecte. Si volem mantenir l'ordenació que hi havia abans de que es refresqui el
    // llistat de formularis, llevar les dues línies següents:
    setOrder(defaultSortOrder);
    setOrderBy('createdAt');
  }, [enrolmentsData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCheck = (id, descriptiveName) => {
    handleSelectedEnrolment(id, descriptiveName);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Comprovam si l'usuari clínic és administrador del programa
  const isAdministeredByUser = (programId) => {
    // Si l'usuari és administrador pot eliminar qualsevol enrolment
    if (sessionContext.role === 'Admin') return true;
    // Si l'usuari no és administrador, només pot eliminar els enrolments dels programes que administra
    return administeredProgramsList.includes(programId);
  };

  const enrolmentsDataColumns = 6; // Per saber el nombre de propietats de cada objecte

  return (
    <TableContainer component={Paper}>
      <Table aria-label='enrolment list table'>
        <TableHead>
          <TableRow>
            <TableCell colSpan={enrolmentsDataColumns + 1} align='left' padding='none'>
              {(selectedEnrolments.length > 0 ? (
                <DeleteToolBar numSelected={selectedEnrolments.length} handleClick={handleRemove} />
              ) : (
                <Typography variant='h5' component='div' className={classesTable.title}>{translations['_patientEnrolments']}</Typography>
              ))}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow className={bgColors.tableRowHeaderBgColor}>
            {/* ATENCIÓ: no pintam la columna _id */}
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'createdAt'}
                direction={orderBy === 'createdAt' ? order : 'asc'}
                onClick={() => handleRequestSort('createdAt')}
              >
                <strong>{translations['_createdAt']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'><strong>{translations['_active']}</strong></TableCell>
            <TableCell align='left'><strong>{translations['_carers']}</strong></TableCell>
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'name'}
                direction={orderBy === 'name' ? order : 'asc'}
                onClick={() => handleRequestSort('name')}
              >
                <strong>{translations['_name']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'sharedId'}
                direction={orderBy === 'sharedId' ? order : 'asc'}
                onClick={() => handleRequestSort('sharedId')}
              >
                <strong>{translations['_sharedId']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='center' />{/* Columna per al botó d'editar */}
            <TableCell align='center'><Delete className={classesField.iconBody} /></TableCell>
          </TableRow>
          {(rowsPerPage > 0
            ? stableSort(adaptedEnrolmentsData, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : stableSort(adaptedEnrolmentsData, getComparator(order, orderBy))
          ).map((data, index) => (
            <TableRow key={data._id} className={index % 2 ? bgColors.tableRowBgColor1 : bgColors.tableRowBgColor2} hover>
              {/* ATENCIÓ: no pintam la columna _id */}
              <TableCell align='left'>{data.createdAt && moment(data.createdAt).format('DD-MM-YYYY')}</TableCell>
              <TableCell align='left'>{data.active ? translations['_yes'] : translations['_no'] }</TableCell>
              <TableCell align='left'>{data.carers}</TableCell>
              <TableCell align='left'>{translations[data.program.sharedId] || data.program.name}</TableCell>              
              <TableCell align='left'>{data.program.sharedId}</TableCell>
              <TableCell align='center'>
                <IconButton onClick={() => handleEditEnrolment(data._id)}>
                  <Edit />
                </IconButton>
              </TableCell>
              <TableCell align='center'>
                <CustomCheckBox
                  disabled={!isAdministeredByUser(data.program._id)}
                  checked={selectedEnrolments.findIndex((item) => item.id === data._id) > -1} // Si res.value es dins la taula la condició dona true
                  onClick={() => handleCheck(data._id, data.program.name)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={enrolmentsDataColumns + 1}
              count={enrolmentsData.length}
              rowsPerPageOptions={rowsPerPageOptions}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              labelDisplayedRows={({ from, to, count }) => from + ' - ' + ((to !== -1 && to) || count) + ' ' + translations['_of'] + ' ' + ((count !== -1 && count) || (translations['_moreThan'] + ' ') + to)}
              labelRowsPerPage={translations['_rowsPerPage']}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

// Indica i controla els props que se han de passar al component
PatientEnrolmentList.propTypes = {
  administeredProgramsList: PropTypes.array.isRequired,
  handleEditEnrolment: PropTypes.func.isRequired,
  handleRemove: PropTypes.func.isRequired,
  handleSelectedEnrolment: PropTypes.func.isRequired,
  enrolmentsData: PropTypes.array.isRequired,
  selectedEnrolments: PropTypes.array.isRequired,
};
